<template>
    <article class="package-detail">
        <BcnBreadcrumbs
            :base-url="runtimeConfig.public.BASE_URL"
            :parents="breadcrumbParents"
            :current="{
                name: packageStory.name,
                full_slug: packageStory.full_slug,
            }"
        />

        <div ref="summaryElement">
            <PackageSummary
                :title="packageStory.name"
                :subtitle="content.subtitle"
                :duration="content.duration"
                :category="categoryLabel"
                :from-price-label="quote.fromPriceLabel.value"
                :whatsapp-href="summaryWhatsappHref"
                @plan="openBooking('package-summary')"
                @ask-expert="trackSummaryWhatsapp"
            />
        </div>

        <div class="package-detail__main">
            <div class="package-detail__content">
                <PhotoGallery
                    :featured="content.image"
                    :gallery="content.gallery"
                    :alt-base="packageStory.name"
                    :label="`Photographs of ${packageStory.name}`"
                    priority
                    @expand="trackGalleryOpened"
                />

                <section
                    v-if="content.description"
                    class="package-detail__overview"
                >
                    <h2 class="k-heading-3">
                        Package overview
                    </h2>
                    <div
                        class="package-detail__richtext k-prose"
                        v-html="renderRichText(content.description)"
                    />
                </section>

                <KFactList
                    v-if="packageFacts.length"
                    class="package-detail__facts"
                    :items="packageFacts"
                />

                <PackageItinerary :days="content.itinerary || []" />
                <PackageChecklist
                    :included="includedItems"
                    :excluded="excludedItems"
                />

                <section
                    v-if="content.booking_terms || content.pricing_note"
                    class="package-detail__terms"
                >
                    <h2 class="k-heading-3">
                        Pricing and booking terms
                    </h2>
                    <p v-if="content.pricing_note">
                        {{ content.pricing_note }}
                    </p>
                    <div
                        v-if="content.booking_terms"
                        class="package-detail__richtext k-prose"
                        v-html="renderRichText(content.booking_terms)"
                    />
                </section>

                <section
                    v-if="content.faqs?.length"
                    class="package-detail__faqs"
                >
                    <h2 class="k-heading-3">
                        Frequently asked questions
                    </h2>
                    <StoryblokComponent
                        :blok="{ component: 'faqs', items: content.faqs, _uid: 'package-faqs' }"
                    />
                </section>
            </div>

            <aside
                ref="widgetElement"
                class="package-detail__sidebar"
            >
                <PackageBookingWidget
                    :package-name="packageStory.name"
                    :quote="quote"
                    :content="content"
                    :whatsapp-number="whatsappNumber"
                    @book="openBooking('package-booking', 2)"
                />
            </aside>
        </div>

        <RecommendedPackages :packages="recommendedPackages" />

        <PackageStickyCta
            :quote="quote"
            :visible="stickyCtaVisible"
            @book="openBooking('package-sticky-bar')"
        />

        <BookingModal
            v-model:open="showBooking"
            :booking="quote.bookingDetails.value"
            item-label="Package"
            extras-label="Package options"
            subject="package-booking"
            quote-title="Plan this holiday"
            :initial-step="bookingStep"
            :can-continue="quote.canBook.value"
            :block-reason="quote.bookingBlockReason.value"
        >
            <template #quote>
                <PackageQuoteForm
                    :quote="quote"
                    :addons="content.addons || []"
                    :pricing-note="content.pricing_note"
                />
            </template>
        </BookingModal>
    </article>
</template>

<script setup>
import { BcnBreadcrumbs } from '@vemedia/bcn-components'
import { KFactList } from '@vemedia/karibu'
import { PACKAGE_CATEGORIES } from '#shared/package-categories'
import { checklistItems } from '#shared/storyblok-checklist'
import BookingModal from '~/components/booking-modal/index.vue'
import PhotoGallery from '~/components/photo-gallery/photo-gallery.vue'
import { usePackageQuote } from '~/composables/usePackageQuote'
import seoTitle from '~/helpers/seoTitle'
import PackageBookingWidget from './package-booking-widget.vue'
import PackageChecklist from './package-checklist.vue'
import PackageItinerary from './package-itinerary.vue'
import PackageQuoteForm from './package-quote-form.vue'
import PackageStickyCta from './package-sticky-cta.vue'
import PackageSummary from './package-summary.vue'
import RecommendedPackages from './recommended-packages.vue'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})

const { data: config } = await useConfig()
const { capture } = useTracking()
const runtimeConfig = useRuntimeConfig()

const { article: packageStory, recommendedPackages = [] } = data
const content = packageStory.content
const includedItems = checklistItems(content.checklist_items, 'included')
const excludedItems = checklistItems(content.checklist_items, 'excluded')
const categorySlug = packageStory.full_slug.split('/')[1] || ''
const categoryLabel = PACKAGE_CATEGORIES[categorySlug] || ''
const packageFacts = computed(() => [
    { label: 'Duration', value: content.duration },
    { label: 'Ideal for', value: content.ideal_for },
    { label: 'Accommodation', value: content.accommodation_tier },
].filter(fact => fact.value))
const pageUrl = `${runtimeConfig.public.BASE_URL}/${packageStory.full_slug.replace(/\/$/, '')}`
const title = seoTitle(packageStory)

// One quote for the whole page. The sidebar widget, the dialog's first step and the sticky
// bar all render against this instance, so a change made on any of them is reflected
// everywhere else and the numbers can never diverge.
const quote = usePackageQuote({
    pricing: content.pricing || [],
    addons: content.addons || [],
    depositPercentage: content.deposit_percentage,
    priceBasis: content.price_basis,
    name: packageStory.name,
    itemUuid: packageStory.uuid,
    pageUrl,
})

const breadcrumbParents = computed(() => [
    {
        name: config.value?.brand_name,
        full_slug: '',
    },
    {
        name: 'Packages',
        full_slug: 'packages',
    },
    ...(categoryLabel
        ? [{ name: categoryLabel, full_slug: `packages/${categorySlug}` }]
        : []),
])

const whatsappNumber = computed(() => config.value?.phone_number || '')
const summaryWhatsappText = computed(() => [
    `Hi! I'd like to ask about ${packageStory.name}.`,
    pageUrl,
].join('\n'))
const summaryWhatsappHref = computed(() => whatsappNumber.value
    ? `https://wa.me/${whatsappNumber.value}?text=${encodeURIComponent(summaryWhatsappText.value)}`
    : '')

const showBooking = ref(false)

// Which step the dialog opens on depends on where it was opened from: the sidebar widget
// already shows the configurator, so sending its visitors back through step one would ask
// them to fill in what they just filled in. Every other surface starts at step one.
const bookingStep = ref(1)

function openBooking(placement, step = 1) {
    bookingStep.value = step
    showBooking.value = true
    capture('booking_started', {
        channel: 'email',
        placement,
        package: packageStory.name,
        total: quote.total.value,
        guests: quote.totalGuests.value,
    })
}

function trackGalleryOpened({ total }) {
    capture('package_gallery_opened', {
        package: packageStory.name,
        image_count: total,
    })
}

function trackSummaryWhatsapp() {
    capture('whatsapp_click', {
        placement: 'package-summary',
        package: packageStory.name,
    })
}

// The sticky bar stands in for a call to action that has scrolled away, so it waits for
// both to be true: the title block has gone, and the sidebar's own buttons are not on
// screen. The first condition keeps the bar off the top of a mobile page, where the
// sidebar is below the fold from the start.
const summaryElement = useTemplateRef('summaryElement')
const widgetElement = useTemplateRef('widgetElement')
const summaryVisible = useElementVisibility(summaryElement)
const widgetVisible = useElementVisibility(widgetElement)
const stickyCtaVisible = computed(() => !summaryVisible.value && !widgetVisible.value)

const stickyCtaReported = ref(false)
watch(stickyCtaVisible, (isVisible) => {
    if (!isVisible || stickyCtaReported.value)
        return

    stickyCtaReported.value = true
    capture('sticky_cta_shown', {
        placement: 'package-sticky-bar',
        package: packageStory.name,
        from_price: quote.fromPrice.value,
    })
})

// Debounced so dragging a stepper reports one quote rather than ten. Owned here, next to
// the quote itself, so adding a surface can never double-report it.
let quoteTimer
watch(
    [quote.travelDate, quote.tier, quote.adults, quote.children, quote.selectedAddons],
    () => {
        clearTimeout(quoteTimer)
        quoteTimer = setTimeout(() => {
            capture('quote_calculated', {
                package: packageStory.name,
                tier: quote.tier.value || null,
                adults: quote.adults.value,
                children: quote.children.value,
                total: quote.total.value,
            })
        }, 800)
    },
)

onBeforeUnmount(() => clearTimeout(quoteTimer))

const descriptionText = computed(() => content.description
    ? renderRichText(content.description).replaceAll(/<[^>]*>?/g, '').replace(/\s+/g, ' ').trim()
    : '')
const metaDescription = computed(() => content.meta_description || descriptionText.value.slice(0, 160))
const galleryImages = computed(() => [content.image, ...(content.gallery || [])]
    .filter(image => image?.filename)
    .map(image => `${image.filename}/m/1200x630`))

const structuredData = computed(() => {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': title,
        'description': metaDescription.value,
        'image': galleryImages.value,
        'brand': {
            '@type': 'Brand',
            'name': config.value?.brand_name,
        },
    }

    if (quote.fromPrice.value !== null) {
        schema.offers = {
            '@type': 'Offer',
            'price': quote.fromPrice.value,
            'priceCurrency': 'USD',
            'availability': 'https://schema.org/InStock',
            'url': pageUrl,
        }
    }

    return JSON.stringify(schema)
})

// Only ever opt out. The host-level rule must keep previews and staging out of search.
// `noindex, follow` and not `useRobotsRule(false)`: `false` resolves to the module's
// `robotsDisabledValue` (`noindex, nofollow`), whose nofollow also cuts the crawl path to
// any indexable children.
if (content.index === false)
    useRobotsRule('noindex, follow')

useHead({
    title,
    meta: [
        { hid: 'description', name: 'description', content: metaDescription.value },
        { property: 'og:type', content: 'product' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:title', content: title },
        { property: 'og:description', content: metaDescription.value },
        ...(content.image?.filename ? [{ property: 'og:image', content: `${content.image.filename}/m/1200x630` }] : []),
        { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { hid: 'twitter:url', name: 'twitter:url', content: pageUrl },
        { hid: 'twitter:title', name: 'twitter:title', content: title },
        { hid: 'twitter:description', name: 'twitter:description', content: metaDescription.value },
        ...(content.image?.filename ? [{ hid: 'twitter:image', name: 'twitter:image', content: `${content.image.filename}/m/1200x630` }] : []),
    ],
    script: [{
        hid: 'structured-data',
        type: 'application/ld+json',
        textContent: structuredData.value,
    }],
})

onMounted(() => {
    capture('package_viewed', {
        package: packageStory.name,
        category: categorySlug || null,
        from_price: quote.fromPrice.value,
    })
})
</script>

<style lang="scss" scoped>
@use '@vemedia/bcn-components/scss/responsive' as *;

.package-detail {
    // The sticky bar overlays the page rather than reserving space, so the page owns the
    // clearance that keeps its last section readable behind it.
    padding-block-end: var(--space-11);

    &__main {
        display: flex;
        gap: var(--space-6);
        align-items: flex-start;

        @include below-desktop {
            flex-direction: column;
            align-items: stretch;
        }
    }

    &__content {
        flex: 1 1 auto;
        min-width: 0;
        max-width: var(--container-2);

        @include below-desktop {
            max-width: none;
        }
    }

    &__sidebar {
        flex: 0 0 360px;
        min-width: 0;

        @include below-desktop {
            width: 100%;
        }
    }

    &__overview > h2,
    &__terms > h2,
    &__faqs > h2 {
        margin-bottom: var(--space-4);
    }

    &__richtext {
        :deep(ul) {
            margin-left: 1.25rem;
        }
    }

    &__facts {
        margin-top: var(--space-5);
    }

    &__terms,
    &__faqs {
        margin-top: var(--space-7);
    }

    &__terms > p {
        margin-bottom: var(--space-4);
        color: var(--color-text-muted);
    }
}
</style>
